'use client';

import { assertUnreachable } from '@bridge/utils';

import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { CollapsibleCard } from '../components/CollapsibleCard';
import type { CollapsibleCardProps } from '../components/CollapsibleCard';
import { FlexContainer } from '../components/FlexContainer';
import { RefreshButton } from '../components/RefreshButton';
import { Heading } from '../typography/Heading';
import { useQueryRefresh } from '../hooks/useQueryRefresh';

import type { QueryKey } from '@tanstack/react-query';

export type TableSectionLayoutVariant = 'card' | 'collapsible' | 'plain';

export interface TableSectionRefreshConfig {
  tooltip?: string;
}

export interface TableSectionRefreshContext {
  refresh: () => void;
  isRefreshing: boolean;
}

export interface TableSectionProps {
  title: React.ReactNode;
  /** React Query key for the table data source — refresh invalidates this key. */
  queryKey: QueryKey;
  /** When true, or when a config object is passed, render a refresh control wired to `queryKey`. */
  refresh?: boolean | TableSectionRefreshConfig;
  /** Extra header controls; receives `{ refresh, isRefreshing }` for coordinating with refresh. */
  headerActions?: React.ReactNode | ((ctx: TableSectionRefreshContext) => React.ReactNode);
  layout?: TableSectionLayoutVariant;
  /** Forwarded to `CollapsibleCard` when `layout="collapsible"`. */
  collapsibleDefaultOpen?: boolean;
  /** Forwarded to `CollapsibleCard` when `layout="collapsible"`. */
  collapsibleHeaderVariant?: CollapsibleCardProps['headerVariant'];
  children: React.ReactNode;
}

function renderTitle(title: React.ReactNode): React.ReactNode {
  return typeof title === 'string' ? <Heading size="sm">{title}</Heading> : title;
}

function resolveRefreshTooltip(title: React.ReactNode, refresh?: boolean | TableSectionRefreshConfig): string | null {
  if (!refresh) return null;
  if (typeof refresh === 'object' && refresh.tooltip) return refresh.tooltip;
  if (typeof title === 'string') return `Refresh ${title}`;
  return 'Refresh';
}

function HeaderControls({
  refreshButton,
  headerActions,
}: {
  refreshButton: React.ReactNode;
  headerActions: React.ReactNode;
}) {
  if (!refreshButton && !headerActions) return null;

  return (
    <FlexContainer className="shrink-0 gap-2" items="center">
      {refreshButton}
      {headerActions}
    </FlexContainer>
  );
}

/**
 * Section shell for titled tables with optional refresh and header actions.
 * Used directly for composable tables and by sectioned `createTableSuspenseWrapper` instances.
 */
export function TableSection({
  title,
  queryKey,
  refresh = false,
  headerActions,
  layout = 'card',
  collapsibleDefaultOpen,
  collapsibleHeaderVariant,
  children,
}: TableSectionProps): React.ReactElement {
  const refreshTooltip = resolveRefreshTooltip(title, refresh);
  const { isRefreshing, refresh: refreshFn } = useQueryRefresh(queryKey);
  const refreshContext: TableSectionRefreshContext = { refresh: refreshFn, isRefreshing };

  const resolvedHeaderActions =
    typeof headerActions === 'function' ? headerActions(refreshContext) : headerActions;

  const refreshButton =
    refreshTooltip != null ? (
      <RefreshButton isFetching={isRefreshing} onClick={refreshFn} tooltip={refreshTooltip} />
    ) : null;

  const controls = <HeaderControls headerActions={resolvedHeaderActions} refreshButton={refreshButton} />;

  switch (layout) {
    case 'plain':
      return (
        <FlexContainer className="gap-4" direction="column" items="stretch">
          <FlexContainer className="gap-2" items="center" justify="between">
            {renderTitle(title)}
            {controls}
          </FlexContainer>
          {children}
        </FlexContainer>
      );

    case 'collapsible':
      return (
        <CollapsibleCard
          defaultOpen={collapsibleDefaultOpen}
          headerActions={controls}
          headerVariant={collapsibleHeaderVariant}
          title={title}
        >
          {children}
        </CollapsibleCard>
      );

    case 'card':
      return (
        <Card>
          <CardHeader>
            <FlexContainer className="gap-2" items="center" justify="between">
              <CardTitle>{renderTitle(title)}</CardTitle>
              {controls}
            </FlexContainer>
          </CardHeader>
          <CardContent>{children}</CardContent>
        </Card>
      );

    default:
      return assertUnreachable(layout);
  }
}
